import { AnalyticIdentifier } from '@common/neo.tax';
import { Button, PageHeader, Stack, Table, TableContainer, useModal } from '@ui-components/neo.tax';
import { ReactComponent as DownloadIcon } from '@ui-components/neo.tax/dist/icons/download.svg';
import React, { useMemo, useState } from 'react';

import { GetCompaniesQueryCompany } from '@neo.tax/shared/hooks/useCompanies';
import { OrganizationInvitation, OrganizationMember } from '@neo.tax/shared/hooks/useCurrentUser';
import { useExportOrganizationMembers } from '@neo.tax/shared/hooks/useExportOrganizationMembers';
import { useAnalyticId } from '@neo.tax/shared/state';
import { OrganizationMembersTableItem, CompanyInvitation } from '@neo.tax/shared/types';
import {
  companyMemberItemScopeDisplayName,
  organizationInvitationToOrganizationMemberTableItem,
  toOrganizationMemberTableItem,
} from '@neo.tax/shared/utils';
import { FilterSortDataType } from '@neo.tax/shared/utils/enums/filter-sort.enum';

import { EditOrganizationMemberModal } from '../edit-organization-member-modal';
import { KeywordSearch, useKeywordSearch } from '../keyword-search';
import { OrganizationMemberDrawer } from '../organization-member-drawer';
import { ColumnDefinition, makeSortComparator, SortPopover, SortValueMapper, useSorting } from '../sort-popover';
import { useOrganizationMemberColumns } from './hooks';

type MemberSortableColumn = 'memberName' | 'companyCount' | 'email' | 'scope';

const mapSortValues: SortValueMapper<OrganizationMembersTableItem, MemberSortableColumn> = (column, item) => {
  switch (column) {
    case 'memberName':
      return item.fullName || item.email || null;
    case 'companyCount':
      return item.companies.length;
    case 'email':
      return item.email || null;
    case 'scope':
      return item.scope || null;
  }
  return null;
};

type OrganizationMembersProps = {
  companies: GetCompaniesQueryCompany[];
  organizationId: string;
  members: OrganizationMember[];
  companyInvitations: CompanyInvitation[];
  organizationInvitations: OrganizationInvitation[];
  organizationName: string;
};

export const OrganizationMembers = ({
  companies,
  companyInvitations,
  members,
  organizationId,
  organizationInvitations,
  organizationName,
}: OrganizationMembersProps): React.ReactElement => {
  const { exportMembers, isExporting } = useExportOrganizationMembers({ organizationId, organizationName });
  const {
    isOpen: isInviteMembersOpen,
    setIsOpen: setIsInviteMembersOpen,
    toggle: toggleInviteMembersModal,
  } = useModal();
  const openNewCompanyMemberModalId = useAnalyticId(AnalyticIdentifier.RTC_ACCOUNTANT_OPEN_INVITE_NEW_MEMBER_MODAL);
  const { isOpen: isMemberDrawerOpen, setIsOpen: setIsMemberDrawerOpen } = useModal();
  const [memberId, setMemberId] = useState<string>();

  const columns = useOrganizationMemberColumns({
    setMemberId,
    setIsMemberDrawerOpen,
  });

  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const sortableColumns = useMemo<ColumnDefinition<MemberSortableColumn>[]>(
    () => [
      { name: 'memberName', label: 'Member Name', dataType: FilterSortDataType.TEXT },
      { name: 'companyCount', label: 'Companies', dataType: FilterSortDataType.NUMBER },
      { name: 'email', label: 'Email Address', dataType: FilterSortDataType.TEXT },
      {
        name: 'scope',
        label: 'Role',
        dataType: FilterSortDataType.ENUM,
        enum: Object.entries(companyMemberItemScopeDisplayName).map(([value, label]) => ({ value, label })),
      },
    ],
    [],
  );
  const sortState = useSorting({ columns: sortableColumns, initialSorting: [] });
  const sorting = sortState.sorts.map((s) => ({ field: s.column, direction: s.direction }));

  const data: OrganizationMembersTableItem[] = React.useMemo(() => {
    const allMembers = [
      ...members.map((member) => toOrganizationMemberTableItem(member, companies)),
      ...organizationInvitations.map((organizationInvitation) =>
        organizationInvitationToOrganizationMemberTableItem(organizationInvitation, companyInvitations, companies),
      ),
    ];

    const keyword = searchKeyword?.toLowerCase();
    const filteredMembers = keyword
      ? allMembers.filter(
          (member) =>
            member.fullName.toLowerCase().includes(keyword) ||
            member.email.toLowerCase().includes(keyword) ||
            member.companies.some((company) => company.shortName.toLowerCase().includes(keyword)) ||
            companyMemberItemScopeDisplayName[member.scope].toLowerCase().includes(keyword),
        )
      : allMembers;

    return sorting.length === 0
      ? filteredMembers
      : [...filteredMembers].sort(makeSortComparator(sorting, mapSortValues));
  }, [organizationInvitations, companies, companyInvitations, members, searchKeyword, sorting]);

  const selectedMember = useMemo(() => data.find((member) => member.id === memberId), [data, memberId]);

  return (
    <Stack direction="column" gap={8} fullHeight>
      <PageHeader
        title="Members"
        subtitle="Add people and manage their access."
        trailingContent={
          <Stack direction="row" gap={2}>
            <Button
              variant="white"
              rounded="regular"
              size="base"
              startIcon={<DownloadIcon />}
              isLoading={isExporting}
              onClick={exportMembers}
            >
              Export CSV
            </Button>
            <Button
              variant="primary"
              rounded="regular"
              size="base"
              onClick={toggleInviteMembersModal}
              id={openNewCompanyMemberModalId}
            >
              Invite New Member
            </Button>
          </Stack>
        }
      />
      <Stack gap={5}>
        <KeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
        <SortPopover itemsDescription="members" {...sortState} />
      </Stack>
      <TableContainer>
        <Table<OrganizationMembersTableItem> columns={columns} data={data} />
      </TableContainer>

      <EditOrganizationMemberModal
        organizationId={organizationId}
        isOpen={isInviteMembersOpen}
        setIsOpen={setIsInviteMembersOpen}
      />
      <OrganizationMemberDrawer
        organizationId={organizationId}
        member={selectedMember}
        setMemberId={setMemberId}
        isOpen={isMemberDrawerOpen}
        setIsOpen={setIsMemberDrawerOpen}
      />
    </Stack>
  );
};
